import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Button, Buttons, CancelButton, DeleteButton, Icon, ModalCard, SingleSelect } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle, useForm } from '~/hooks';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';
import { Header, Page } from '../components';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useAdminSession } from '../contexts/AdminSessionContext';

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: ${weights.bold};
  margin-bottom: 1rem;
`;

const CreateForm = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  padding: 1.5rem;
  background-color: ${colors.grey5};
  border-radius: 0.5rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: ${weights.medium};
  color: ${colors.grey75};
`;

const Input = styled.input`
  width: 15rem;
`;

const SelectWrapper = styled.div`
  width: 10rem;
`;

const CreateButton = styled(Button)`
  width: 14rem;
`;

const WorkspaceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const WorkspaceItem = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  border-radius: 0.5rem;
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
`;

const WorkspaceRow = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  cursor: pointer;
`;

const WorkspaceInfo = styled.a`
  flex: 1;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const WorkspaceName = styled.div`
  font-weight: ${weights.bold};
  font-size: 1rem;
  color: ${colors.black};
`;

const WorkspaceKey = styled.div`
  font-size: 0.875rem;
  color: ${colors.grey55};
`;

const WorkspaceMeta = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 7rem;
`;

const WideMetaItem = styled(MetaItem)`
  width: 10rem;
`;

const MetaValue = styled.div`
  font-weight: ${weights.medium};
`;

const MetaLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

const Tag = styled.span`
  display: inline-flex;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  border-radius: 0.25rem;
  background-color: ${colors.primary10};
  color: ${colors.accent};
`;

const DeleteIcon = styled(Icon)`
  margin-left: 1rem;
  color: ${colors.danger};
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    color: ${colors.danger75};
  }
`;

const ExpandIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 1rem;
  color: ${colors.grey25};
  transform: rotate(-90deg);
  transition: transform 0.1s;

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(0deg);
    `}
`;

const WorkspacePanel = styled.div`
  padding: 0 1.25rem 1rem;
`;

const EmptyPersonas = styled.div`
  padding: 0.75rem 1rem;
  color: ${colors.grey55};
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  padding: 3rem;
  text-align: center;
  color: ${colors.grey55};
  background-color: ${colors.grey5};
  border-radius: 0.5rem;
`;

const LoadingState = styled.div`
  padding: 3rem;
  text-align: center;
  color: ${colors.grey55};
`;

const PersonaList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.grey10};
  border-radius: 0.5rem;
  overflow: hidden;
`;

const PersonaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.grey10};
  }
`;

const PersonaInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const PersonaRole = styled.div`
  font-weight: ${weights.medium};
  font-size: 0.875rem;
`;

const PersonaEmail = styled.div`
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

const PersonaActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export default function DemoWorkspaces() {
  useDocumentTitle('Demo Workspaces - Admin');

  const api = useApi();
  const toast = useToast();
  const { impersonate } = useImpersonation();
  const { navigateWorkspace } = useWorkspace();
  const { can } = useAdminSession();

  const [{ isSubmitting }, form] = useForm();
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [templateKey, setTemplateKey] = useState('sales');
  const [suffix, setSuffix] = useState('');
  const [openRows, setOpenRows] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState();

  const loadWorkspaces = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.admin.demoWorkspaces.get();
      setWorkspaces(data);
    } catch (error) {
      toast.error('Failed to load demo workspaces');
    } finally {
      setIsLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  const toggleRow = useCallback((workspaceId) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(workspaceId) ? next.delete(workspaceId) : next.add(workspaceId);
      return next;
    });
  }, []);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const { data } = await api.admin.demoWorkspaces.create({
        templateKey,
        suffix: suffix || undefined,
      });
      setSuffix('');
      await loadWorkspaces();
      toast.success(`${data.workspace.name} created successfully`);
      setOpenRows((prev) => new Set([...prev, data.workspace.id]));
    } catch (error) {
      toast.error(error.message || 'Failed to create demo workspace');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    form.submit();
    try {
      await api.admin.demoWorkspaces.delete(deleteTarget.id);
      toast.success('Demo workspace deleted successfully');
      setDeleteTarget();
      await loadWorkspaces();
      form.done();
    } catch ({ message }) {
      form.error({ message });
      toast.error(message || 'Failed to delete demo workspace');
    }
  };

  return (
    <Page>
      <Header />

      <Section>
        <SectionTitle>Create Demo Workspace</SectionTitle>
        <CreateForm>
          <FormField>
            <Label>Template</Label>
            <SelectWrapper>
              <SingleSelect value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
                <option value="sales">Sales</option>
              </SingleSelect>
            </SelectWrapper>
          </FormField>
          <FormField>
            <Label>Suffix (optional)</Label>
            <Input
              type="text"
              placeholder="e.g., acme, client-name"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
            />
          </FormField>
          <Buttons>
            <CreateButton onClick={handleCreate} disabled={isCreating}>
              {isCreating ? <Icon icon="spinner" spin={true} /> : 'Create Demo Workspace'}
            </CreateButton>
          </Buttons>
        </CreateForm>
      </Section>

      <Section>
        <SectionTitle>Demo Workspaces</SectionTitle>
        {isLoading ? (
          <LoadingState>
            <Icon icon="spinner" spin={true} /> Loading...
          </LoadingState>
        ) : workspaces.length === 0 ? (
          <EmptyState>No demo workspaces found. Create one above to get started.</EmptyState>
        ) : (
          <WorkspaceList>
            {workspaces.map((workspace) => (
              <WorkspaceItem key={workspace.id}>
                <WorkspaceRow onClick={() => toggleRow(workspace.id)}>
                  <WorkspaceInfo
                    href={`/app/${workspace.key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}>
                    <WorkspaceName>{workspace.name}</WorkspaceName>
                    <WorkspaceKey>{workspace.key}</WorkspaceKey>
                  </WorkspaceInfo>
                  <WorkspaceMeta>
                    <MetaItem>
                      <MetaValue>
                        <Tag>{workspace.template || 'unknown'}</Tag>
                      </MetaValue>
                      <MetaLabel>template</MetaLabel>
                    </MetaItem>
                    <MetaItem>
                      <MetaValue>{workspace.memberCount}</MetaValue>
                      <MetaLabel>{workspace.memberCount === 1 ? 'member' : 'members'}</MetaLabel>
                    </MetaItem>
                    <MetaItem>
                      <MetaValue>{moment(workspace.createdAt).format(dateFormats.compactDate)}</MetaValue>
                      <MetaLabel>created</MetaLabel>
                    </MetaItem>
                    {workspace.createdBy && (
                      <WideMetaItem>
                        <MetaValue>{workspace.createdBy.name}</MetaValue>
                        <MetaLabel>created by</MetaLabel>
                      </WideMetaItem>
                    )}
                  </WorkspaceMeta>
                  <DeleteIcon
                    icon="trash"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(workspace);
                    }}
                  />
                  <ExpandIndicator isOpen={openRows.has(workspace.id)}>
                    <Icon icon="angle-down" />
                  </ExpandIndicator>
                </WorkspaceRow>
                {openRows.has(workspace.id) && (
                  <WorkspacePanel>
                    {workspace.personaMembers?.length > 0 ? (
                      <PersonaList>
                        {workspace.personaMembers.map((m) => (
                          <PersonaRow key={m.id}>
                            <PersonaInfo>
                              <PersonaRole>{m.roleName || m.name}</PersonaRole>
                              <PersonaEmail>{m.email}</PersonaEmail>
                            </PersonaInfo>
                            <PersonaActions onClick={(e) => e.stopPropagation()}>
                              {can('impersonation') && (
                                <Button
                                  onClick={async () => {
                                    await impersonate(m.id);
                                    navigateWorkspace(workspace.key);
                                  }}>
                                  Impersonate
                                </Button>
                              )}
                              <Button isOutline onClick={() => window.open(`/app/${workspace.key}`, '_blank')}>
                                Login
                              </Button>
                            </PersonaActions>
                          </PersonaRow>
                        ))}
                      </PersonaList>
                    ) : (
                      <EmptyPersonas>No persona members found for this workspace.</EmptyPersonas>
                    )}
                  </WorkspacePanel>
                )}
              </WorkspaceItem>
            ))}
          </WorkspaceList>
        )}
      </Section>

      {deleteTarget && (
        <ModalCard title="Delete Demo Workspace" onClose={() => setDeleteTarget()}>
          <ModalCard.Body>
            Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This will permanently delete all data
            in this workspace.
          </ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={() => setDeleteTarget()}>Cancel</CancelButton>
              <DeleteButton isLoading={isSubmitting} onClick={handleDelete}>
                Delete
              </DeleteButton>
            </Buttons>
          </ModalCard.Footer>
        </ModalCard>
      )}
    </Page>
  );
}
